import { z } from 'zod';

// Input and output contracts of the Registry module. The same schemas validate HTTP requests and will
// describe agent tools, so humans and agents go through identical validation.

export const IsoDate = z.iso.date();
export const Id = z.uuid();

export const UnitKind = z.enum(['residential', 'commercial', 'parking', 'storage', 'other']);
export const PartyKind = z.enum(['person', 'organization']);
export const Language = z.enum(['fr', 'en']);

export const CreateBuilding = z.object({
  name: z.string().trim().min(1).max(200),
  streetAddress: z.string().trim().min(1).max(300),
  city: z.string().trim().min(1).max(100),
  province: z.string().length(2).default('QC'),
  postalCode: z
    .string()
    .trim()
    .toUpperCase()
    .transform((v) => v.replace(/^([A-Z]\d[A-Z])\s*(\d[A-Z]\d)$/, '$1 $2'))
    .pipe(z.string().regex(/^[A-Z]\d[A-Z] \d[A-Z]\d$/, 'Expected a Canadian postal code, e.g. H2X 1Y4'))
    .optional(),
});
export type CreateBuilding = z.infer<typeof CreateBuilding>;

export const CreateUnit = z.object({
  buildingId: Id,
  label: z.string().trim().min(1).max(50),
  kind: UnitKind,
  cadastralLot: z.string().trim().min(1).max(50).optional(),
  floor: z.int().min(-10).max(200).optional(),
  areaM2: z.number().positive().max(100_000).optional(),
});
export type CreateUnit = z.infer<typeof CreateUnit>;

export const ListUnitsQuery = z.object({ buildingId: Id.optional() });

export const CreateParty = z.object({
  kind: PartyKind,
  displayName: z.string().trim().min(1).max(200),
  email: z.email().optional(),
  phone: z.string().trim().min(7).max(30).optional(),
  preferredLanguage: Language.default('fr'),
});
export type CreateParty = z.infer<typeof CreateParty>;

const optionalText = (max: number) => z.string().trim().max(max).optional().transform((v) => (v ? v : undefined));

/** The board edits a person's record. */
export const UpdateParty = z.object({
  displayName: z.string().trim().min(1).max(200),
  email: z.union([z.email(), z.literal('')]).optional().transform((v) => v || undefined),
  phone: optionalText(30),
  preferredLanguage: Language,
  mailingAddress: optionalText(300),
});
export type UpdateParty = z.input<typeof UpdateParty>;

/** A member updates their own contact details (Law 25: right of rectification). */
export const UpdateOwnContact = UpdateParty.omit({ displayName: true });
export type UpdateOwnContact = z.input<typeof UpdateOwnContact>;

export const AddOccupancy = z.object({ partyId: Id, from: IsoDate });
export type AddOccupancy = z.infer<typeof AddOccupancy>;
export const EndOccupancy = z.object({ to: IsoDate });

const Share = z
  .object({ numerator: z.int().positive(), denominator: z.int().positive() })
  .refine((s) => s.numerator <= s.denominator, 'A share cannot exceed 1');

/** Exact rational arithmetic: floating point would accept 1/3 + 1/3 + 1/3 only by luck. */
export function sharesSumToOne(shares: readonly { numerator: number; denominator: number }[]): boolean {
  let num = 0n;
  let den = 1n;
  for (const s of shares) {
    num = num * BigInt(s.denominator) + BigInt(s.numerator) * den;
    den *= BigInt(s.denominator);
  }
  return num === den;
}

export const SetOwnership = z.object({
  effectiveFrom: IsoDate,
  owners: z
    .array(z.object({ partyId: Id, share: Share }))
    .min(1)
    .refine((o) => new Set(o.map((x) => x.partyId)).size === o.length, 'Each owner may appear only once'),
});
export type SetOwnership = z.infer<typeof SetOwnership>;

export const AsOfQuery = z.object({ asOf: IsoDate.optional() });

export const CreateQuotePartSchedule = z.object({
  effectiveFrom: IsoDate,
  denominator: z.int().positive(),
  source: z.string().trim().min(1).max(500),
  parts: z
    .array(z.object({ unitId: Id, numerator: z.int().positive() }))
    .min(1)
    .refine((p) => new Set(p.map((x) => x.unitId)).size === p.length, 'Each unit may appear only once'),
});
export type CreateQuotePartSchedule = z.infer<typeof CreateQuotePartSchedule>;

export interface Building {
  id: string;
  name: string;
  streetAddress: string;
  city: string;
  province: string;
  postalCode: string | null;
}

export interface Unit {
  id: string;
  buildingId: string;
  label: string;
  kind: z.infer<typeof UnitKind>;
  cadastralLot: string | null;
  floor: number | null;
  areaM2: number | null;
}

export interface Party {
  id: string;
  kind: z.infer<typeof PartyKind>;
  displayName: string;
  email: string | null;
  phone: string | null;
  preferredLanguage: z.infer<typeof Language>;
  mailingAddress: string | null;
  /** Linked to a sign-in account. */
  hasAccount: boolean;
}

export interface Occupancy {
  id: string;
  unitId: string;
  partyId: string;
  from: string;
  to: string | null;
}

/** Everything the board sees about a person: record, units owned (with history), units occupied. */
export interface PartyProfile {
  party: Party;
  userId: string | null;
  ownership: { unitId: string; unitLabel: string; share: { numerator: number; denominator: number }; from: string; to: string | null }[];
  occupancy: (Occupancy & { unitLabel: string })[];
}

export interface Ownership {
  unitId: string;
  partyId: string;
  share: { numerator: number; denominator: number };
  from: string;
  to: string | null;
}

export interface QuotePartSchedule {
  id: string;
  status: 'draft' | 'active';
  from: string;
  to: string | null;
  denominator: number;
  source: string;
  parts: { unitId: string; numerator: number }[];
}
